import { IssueList, Labelled, NumberInput, SegmentedControl, Worked, smallButtonClass } from '@brush/ui';
import {
  RATING_PRESETS,
  coverageIssue,
  periodRanges,
  setPeriodCount,
  setPeriodLength,
  type RatingAggregationDraft,
  type RatingAggregationMode,
  type RatingWindow,
} from '../../ratings/builder';

/**
 * §9.3's period table — the hardest thing on the rating builder's screen, and the one an operator
 * cannot reason about without help.
 *
 * <b>Every row renders its own date range, computed from today.</b> §9.3 says so outright, and the
 * reason is that "period 1" is a number with no meaning: an operator deciding whether 0.7 is the
 * right weight for period 4 is deciding what a result from the first half of 2024 should be worth,
 * and only one of those two sentences is a question they can answer. The arithmetic behind it
 * belongs to `periodRanges`, which mirrors the engine's own calendar, because a table describing
 * different months from the ones the engine fills is worse than a table with no dates at all.
 *
 * <b>Adding or removing a period moves the window in lockstep</b> (§9.3, §4.4). That is the path
 * lockstep covers; what it cannot cover — a window length typed by hand, or a board that arrived
 * with a stored mismatch — is reported immediately rather than at save, naming both numbers,
 * because by the time a 400 comes back the operator has moved on from the table that caused it.
 *
 * <b>A preset writes the array and is not stored.</b> There is no fourth aggregation mode hiding in
 * the three buttons: §3.5 normalises three modes into one period array at write time and the engine
 * never branches on the value, so a stored preset id would be a second answer to what the board
 * does — free to disagree with the table edited straight afterwards.
 */
export function RatingPeriodTable({
  window,
  aggregation,
  onChange,
  asOf,
  readOnly,
}: {
  window: RatingWindow;
  aggregation: RatingAggregationDraft;
  onChange: (next: { window: RatingWindow; aggregation: RatingAggregationDraft }) => void;
  /** §2.2's anchor — today, unless somebody is looking at a past one. */
  asOf: string;
  readOnly: boolean;
}) {
  const ranges = periodRanges(window, effectiveLength(window, aggregation), aggregation.periods.length, asOf);
  const coverage = coverageIssue(window, aggregation);

  const setMode = (mode: RatingAggregationMode) =>
    onChange({
      window,
      aggregation: {
        ...aggregation,
        mode,
        // sumAll is one period spanning the window (§4.5), so switching to it collapses the table
        // rather than leaving five rows the document will not carry. Switching away from it leaves
        // that one row, which the operator then adds to — the period LENGTH is what they set next,
        // and seeding six rows they did not ask for would be the preset button's job, not a
        // dropdown's.
        periods:
          mode === 'sumAll'
            ? [{ index: 1, takeAll: true, n: null, decay: 1 }]
            : aggregation.periods.map((period) =>
                // §4.5: sumWithDecay keeps every result in each period, and the server REFUSES an
                // `n` under it rather than discarding one. Flipping the rows here is what stops a
                // mode switch producing a board that cannot be saved.
                mode === 'sumWithDecay' ? { ...period, takeAll: true } : period,
              ),
      },
    });

  const setPeriod = (index: number, patch: Partial<RatingAggregationDraft['periods'][number]>) =>
    onChange({
      window,
      aggregation: {
        ...aggregation,
        periods: aggregation.periods.map((period) =>
          period.index === index ? { ...period, ...patch } : period,
        ),
      },
    });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="mb-1 block text-xs text-ink-800/60">How results are counted</span>
          <SegmentedControl
            ariaLabel="How results are counted"
            value={aggregation.mode}
            onChange={(mode) => !readOnly && setMode(mode)}
            options={[
              { value: 'sumAll', label: 'Every result' },
              { value: 'sumWithDecay', label: 'Every result, ageing' },
              { value: 'bestNPerPeriod', label: 'Best few per period' },
            ]}
          />
        </div>

        {!readOnly ? (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] text-ink-800/50">Start from:</span>
            {RATING_PRESETS.map((preset) => (
              <button
                key={preset.id}
                type="button"
                title={preset.detail}
                onClick={() => onChange(preset.apply(window))}
                className={smallButtonClass}
              >
                {preset.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <p className="text-[11.5px] leading-relaxed text-ink-800/60">{modeExplanation(aggregation.mode)}</p>

      {aggregation.mode === 'sumAll' ? (
        /*
         * §4.5: a sumAll board normalises to a single period taking the WINDOW's own length, so
         * there is no table to edit and no period length to set — and showing an editable one-row
         * table would invite an operator to change a number that the document ignores.
         */
        <Worked>
          Every result inside the window counts once, at full weight, however old it is. There are no
          periods to weigh — the window above is the whole of it.
        </Worked>
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-4">
            <Labelled label="Each period is" className="max-w-[9rem]">
              <NumberInput
                value={aggregation.periodLengthMonths}
                onChange={(months) =>
                  onChange(setPeriodLength(window, aggregation, months === null ? null : Math.max(1, months)))
                }
                ariaLabel="Months in each period"
                min={1}
                step={1}
                disabled={readOnly}
                className="w-full"
              />
            </Labelled>
            <span className="pb-2 text-xs text-ink-800/55">months long</span>

            {!readOnly ? (
              <div className="flex gap-2 pb-1">
                <button
                  type="button"
                  className={smallButtonClass}
                  onClick={() =>
                    onChange(setPeriodCount(window, aggregation, aggregation.periods.length + 1))
                  }
                >
                  Add a period
                </button>
                <button
                  type="button"
                  className={smallButtonClass}
                  disabled={aggregation.periods.length <= 1}
                  onClick={() =>
                    onChange(setPeriodCount(window, aggregation, aggregation.periods.length - 1))
                  }
                >
                  Remove the oldest
                </button>
              </div>
            ) : null}
          </div>

          {/* Wide content scrolls inside its own region rather than pushing the dialog sideways. */}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[34rem] text-[12px]">
              <thead>
                <tr className="border-b border-line text-left text-[10.5px] uppercase tracking-wide text-ink-800/45">
                  <th className="py-1.5 pr-3 font-medium">Period</th>
                  <th className="py-1.5 pr-3 font-medium">Covers</th>
                  <th className="py-1.5 pr-3 font-medium">Counts</th>
                  <th className="py-1.5 font-medium">Worth</th>
                </tr>
              </thead>
              <tbody>
                {aggregation.periods.map((period) => {
                  const range = ranges.find((r) => r.index === period.index);

                  return (
                    <tr key={period.index} className="border-b border-line/60 last:border-b-0">
                      <td className="py-2 pr-3 align-top">
                        <span className="num text-ink-800/60">{period.index}</span>
                        {period.index === 1 ? (
                          <span className="ml-1.5 text-[10px] text-ink-800/40">most recent</span>
                        ) : null}
                      </td>

                      {/*
                        §9.3's whole point. The dates, not the index — an operator weighing period 4
                        is weighing the first half of 2024, and only that sentence is answerable.
                      */}
                      <td className="num py-2 pr-3 align-top text-[11.5px] text-ink-950">
                        {range ? (range.from === null ? `up to ${range.to}` : `${range.from} – ${range.to}`) : '—'}
                      </td>

                      <td className="py-2 pr-3 align-top">
                        {aggregation.mode === 'sumWithDecay' ? (
                          // §4.5: every result, and the server refuses an `n` here — so there is no
                          // control rather than a control that produces an unsaveable board.
                          <span className="text-[11.5px] text-ink-800/55">every result</span>
                        ) : (
                          <div className="flex items-center gap-2">
                            <SegmentedControl
                              ariaLabel={`What counts in period ${period.index}`}
                              value={period.takeAll ? 'all' : 'best'}
                              onChange={(next) =>
                                !readOnly &&
                                setPeriod(period.index, {
                                  takeAll: next === 'all',
                                  // Seeded rather than left blank, so switching to "best" does not
                                  // land the operator on a validation error they did not cause.
                                  n: next === 'all' ? period.n : period.n ?? 5,
                                })
                              }
                              options={[
                                { value: 'all', label: 'All' },
                                { value: 'best', label: 'Best' },
                              ]}
                            />
                            {!period.takeAll ? (
                              <NumberInput
                                value={period.n}
                                onChange={(n) => setPeriod(period.index, { n })}
                                ariaLabel={`How many results count in period ${period.index}`}
                                min={1}
                                step={1}
                                disabled={readOnly}
                                className="w-16"
                              />
                            ) : null}
                          </div>
                        )}
                      </td>

                      <td className="py-2 align-top">
                        <div className="flex items-center gap-2">
                          <NumberInput
                            value={period.decay}
                            onChange={(decay) => setPeriod(period.index, { decay })}
                            ariaLabel={`What a result in period ${period.index} is worth`}
                            min={0}
                            step={0.05}
                            disabled={readOnly}
                            className="w-20"
                          />
                          {/* The figure an operator actually reads. 0.7 means nothing; "70% of a
                              recent result" is the judgement they are making. */}
                          <span className="text-[11px] text-ink-800/45">
                            {period.decay === null ? '—' : `${Math.round(period.decay * 100)}% of full`}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* §9.3: immediately, not at save — and naming both numbers, because what the operator has to
          decide is which of the two they meant. */}
      <IssueList issues={coverage ? [coverage] : []} />
    </div>
  );
}

/**
 * §4.5: a `sumAll` board's single period takes the WINDOW's own length rather than a stored one, so
 * the table has to render it from the same place the document will.
 */
function effectiveLength(window: RatingWindow, aggregation: RatingAggregationDraft): number | null {
  if (aggregation.mode !== 'sumAll') return aggregation.periodLengthMonths;
  return window.type === 'rolling' ? window.lengthMonths : null;
}

function modeExplanation(mode: RatingAggregationMode): string {
  switch (mode) {
    case 'sumAll':
      return 'Every result in the window counts once, at full weight. The simplest thing a rating can do, and what the default All Tournaments rating does.';
    case 'sumWithDecay':
      return 'Every result counts, but the window is sliced into periods and an older period is worth less. Nobody is cut off, and nobody is penalised for playing a lot.';
    default:
      return 'Only a player’s best few results in each period count, and an older period is worth less. This is how GPI works — it rewards deep runs over volume.';
  }
}
